import React, { useState, useEffect, useRef, useContext, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Grid,
  Button,
  IconButton,
  Paper,
  BottomNavigation,
  BottomNavigationAction,
} from '@mui/material';
import {
  LocationOn,
  ChevronLeft,
  ChevronRight,
  PanTool,
  Checklist,
  Map,
  Phone,
  Email,
  Home,
  QrCode2,
  Person,
  Business,
  Article,
  EmojiEvents,
  Today,
  School,
  LocationOff,
  RefreshRounded,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { AuthContext } from '../context/AuthContext';
import { useCityContext } from '../context/CityContext';
import api from '../utils/api';
import { RADIUS } from '../theme/tokens';
import BannerCarousel from '../components/BannerCarousel';
import LibraryCard from '../components/LibraryCard';
import LibraryCardSkeleton from '../components/LibraryCardSkeleton';
import DesktopNav from '../components/DesktopNav';
import DiscountTile from '../components/publicHome/DiscountTile';
import SearchRadius from '../components/publicHome/SearchRadius';
import CityFilterSection from '../components/publicHome/CityFilterSection';
import BenefitGridSkeleton from '../components/publicHome/BenefitGridSkeleton';

const BenefitGrid = React.lazy(() => import('../components/publicHome/BenefitGrid'));

const MotionBox = motion(Box);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Merge primary (radius-filtered) and fallback (no-radius) results into up to
 * 6 de-duplicated entries. Primary results come first.
 */
function mergeNearbyAndFallback(nearby, fallback) {
  const seen = new Set(nearby.map((l) => l.id));
  const out = [...nearby];
  for (const lib of fallback) {
    if (!seen.has(lib.id)) {
      out.push(lib);
      seen.add(lib.id);
    }
    if (out.length >= 6) break;
  }
  return out.slice(0, 6);
}

/**
 * Public Home Page — Marketing/Landing page accessible to everyone.
 * Shows library listings, features, and information about BookMyLibrary.
 */
function PublicHome() {
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);
  const {
    detectedCity,
    nearbyCities,
    selectedCity,
    selectCity,
    isDetecting,
    effectiveCity,
    includeNearby,
  } = useCityContext();

  // Library fetch state machine: 'loading' | 'success' | 'empty' | 'error'
  const [librariesStatus, setLibrariesStatus] = useState('loading');
  const [librariesError, setLibrariesError] = useState(null);

  // Location state: 'pending' | 'granted' | 'denied' | 'timeout'
  const [locationStatus, setLocationStatus] = useState('pending');

  const [trendingLibraries, setTrendingLibraries] = useState([]);
  const [nearbyCount, setNearbyCount] = useState(0); // raw radius-filtered count for caption
  const [userLocation, setUserLocation] = useState(null);
  const [bottomNavValue, setBottomNavValue] = useState(0);
  const [nearbyRadius, setNearbyRadius] = useState(10); // KM: 2 | 5 | 10 | 15
  const carouselRef = useRef(null);

  // ------------------------------------------------------------------
  // Geolocation — resolve once on mount with a 2500ms timeout
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!navigator.geolocation) {
      setLocationStatus('denied');
      return;
    }

    let resolved = false;

    const timeoutId = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        setLocationStatus('timeout');
      }
    }, 2500);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeoutId);
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          setLocationStatus('granted');
        }
      },
      () => {
        if (!resolved) {
          resolved = true;
          clearTimeout(timeoutId);
          setLocationStatus('denied');
        }
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );

    return () => {
      resolved = true;
      clearTimeout(timeoutId);
    };
  }, []);

  // ------------------------------------------------------------------
  // Fetch effect — waits for location to resolve or time out
  // ------------------------------------------------------------------
  useEffect(() => {
    if (locationStatus === 'pending') return;
    const controller = new AbortController();
    fetchTrendingLibraries(controller.signal);
    return () => controller.abort();
  }, [userLocation, effectiveCity, includeNearby, nearbyRadius, locationStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // ------------------------------------------------------------------
  // fetchTrendingLibraries
  // ------------------------------------------------------------------
  const fetchTrendingLibraries = async (signal) => {
    setLibrariesStatus('loading');
    setLibrariesError(null);

    try {
      // --- Primary params (with radius filter when location available) ---
      const primaryParams = new URLSearchParams();
      primaryParams.append('limit', '6');
      if (userLocation) {
        primaryParams.append('userLat', userLocation.lat);
        primaryParams.append('userLng', userLocation.lng);
        primaryParams.append('nearbyRadius', nearbyRadius);
        primaryParams.append('sortBy', 'distance');
      }
      if (effectiveCity) {
        primaryParams.append('city', effectiveCity);
        primaryParams.append('includeNearby', includeNearby ? 'true' : 'false');
      }

      // --- Fallback params (no radius, broader sort) ---
      const fallbackParams = new URLSearchParams();
      fallbackParams.append('limit', '6');
      if (userLocation) {
        fallbackParams.append('userLat', userLocation.lat);
        fallbackParams.append('userLng', userLocation.lng);
        fallbackParams.append('sortBy', 'distance');
        fallbackParams.append('radius', '500');
        // pass radius=500 to bypass backend's default 10km filter when nearbyRadius is omitted
      } else {
        fallbackParams.append('sortBy', 'rating');
      }
      if (effectiveCity) {
        fallbackParams.append('city', effectiveCity);
        fallbackParams.append('includeNearby', includeNearby ? 'true' : 'false');
      }

      let primaryLibraries = [];
      let fallbackLibraries = [];

      if (userLocation) {
        // Eager parallel: issue both queries simultaneously
        const [primaryRes, fallbackRes] = await Promise.all([
          api.get(`/libraries?${primaryParams.toString()}`, { signal }),
          api.get(`/libraries?${fallbackParams.toString()}`, { signal }),
        ]);
        primaryLibraries = primaryRes.data?.data?.libraries ?? [];
        fallbackLibraries = fallbackRes.data?.data?.libraries ?? [];
      } else {
        const res = await api.get(`/libraries?${primaryParams.toString()}`, { signal });
        primaryLibraries = res.data?.data?.libraries ?? [];
      }

      // Guard: bail if this fetch was cancelled
      if (signal.aborted) return;

      const merged = mergeNearbyAndFallback(primaryLibraries, fallbackLibraries);

      if (merged.length === 0) {
        setLibrariesStatus('empty');
      } else {
        setTrendingLibraries(merged);
        setNearbyCount(primaryLibraries.length);
        setLibrariesStatus('success');
      }
    } catch (err) {
      if (signal.aborted) return;
      setLibrariesError(err.message ?? 'Failed to load libraries');
      setLibrariesStatus('error');
    }
  };

  // ------------------------------------------------------------------
  // Carousel scroll
  // ------------------------------------------------------------------
  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = 400;
      const newPos =
        carouselRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount);
      carouselRef.current.scrollTo({ left: newPos, behavior: 'smooth' });
    }
  };

  // ------------------------------------------------------------------
  // Animation variants
  // ------------------------------------------------------------------
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
  };

  // ------------------------------------------------------------------
  // Nearest Libraries carousel content
  // ------------------------------------------------------------------
  const renderLibrariesCarouselContent = () => {
    if (librariesStatus === 'loading') {
      return [0, 1, 2, 3].map((i) => (
        <Box
          key={`skel-${i}`}
          sx={{ width: { xs: 280, sm: 240, md: 240 }, flexShrink: 0 }}
        >
          <LibraryCardSkeleton />
        </Box>
      ));
    }

    if (librariesStatus === 'error') {
      return (
        <Box
          sx={{
            width: '100%',
            py: 4,
            px: 3,
            borderRadius: RADIUS.xl,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="error" mb={2}>
            {librariesError || 'Something went wrong loading libraries.'}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<RefreshRounded />}
            onClick={() => fetchTrendingLibraries(new AbortController().signal)}
            sx={{ borderRadius: RADIUS.md, textTransform: 'none' }}
          >
            Retry
          </Button>
        </Box>
      );
    }

    if (librariesStatus === 'empty') {
      return (
        <Box
          sx={{
            width: '100%',
            borderRadius: RADIUS.xl,
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            p: { xs: 3, md: 4 },
            textAlign: 'center',
          }}
        >
          <LocationOff sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" fontWeight={700} gutterBottom>
            No libraries yet in {effectiveCity || 'your area'}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            We&apos;re expanding fast. Be the first to hear when libraries open near you, or browse
            all libraries on the platform.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="medium"
              onClick={() => navigate('/libraries')}
              sx={{ borderRadius: RADIUS.md, textTransform: 'none', fontWeight: 600 }}
            >
              View All Libraries
            </Button>
            <Button
              variant="contained"
              size="medium"
              component="a"
              href="mailto:contact@bookmylibrary.in?subject=Register My Library/Institute"
              sx={{ borderRadius: RADIUS.md, textTransform: 'none', fontWeight: 600 }}
            >
              Add Your Library
            </Button>
          </Box>
        </Box>
      );
    }

    // 'success'
    return trendingLibraries.map((library) => (
      <Box
        key={library.id}
        sx={{ width: { xs: 280, sm: 240, md: 240 }, flexShrink: 0 }}
      >
        <LibraryCard library={library} />
      </Box>
    ));
  };

  // Show fallback caption when radius search returned fewer than 3 results but
  // fallback padding kicked in
  const showFallbackCaption =
    librariesStatus === 'success' &&
    nearbyCount < 3 &&
    trendingLibraries.length > nearbyCount &&
    locationStatus === 'granted';

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  return (
    <>
      {/* Navigation */}
      <DesktopNav />

      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          maxWidth: '100vw',
          overflowX: 'hidden',
          position: 'relative',
          bgcolor: 'transparent',
        }}
      >
        {/* Hero Banner */}
        <BannerCarousel userCity={effectiveCity} />

        <Box sx={{ width: '100%', overflowX: 'hidden' }}>
          <Container maxWidth="lg" sx={{ py: { xs: 3, md: 6 }, pb: 10, width: '100%', maxWidth: '100%' }}>
            <MotionBox initial="hidden" animate="visible" variants={containerVariants}>

              {/* Quick Actions */}
              <MotionBox variants={itemVariants} mb={{ xs: 2, md: 3 }}>
                <Grid container spacing={{ xs: 1, md: 2 }}>
                  <Grid item xs={3} sm={3} md={3}>
                    <Box
                      onClick={() => navigate('/libraries?nearMe=true')}
                      sx={{
                        p: { xs: 1, md: 2 },
                        borderRadius: 3,
                        bgcolor: 'background.paper',
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        border: '2px solid',
                        borderColor: 'transparent',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 24px rgba(33, 150, 243, 0.2)',
                          borderColor: 'info.main',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: 32, md: 44 },
                          height: { xs: 32, md: 44 },
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto',
                          mb: { xs: 0.5, md: 1 },
                        }}
                      >
                        <LocationOn sx={{ color: 'white', fontSize: { xs: 18, md: 24 } }} />
                      </Box>
                      <Typography variant="subtitle2" fontWeight="700" fontSize={{ xs: '0.65rem', md: '0.85rem' }}>
                        Near Me
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontSize={{ xs: '0.55rem', md: '0.7rem' }}>
                        Nearby libraries
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={3} sm={3} md={3}>
                    <Box
                      onClick={() => navigate('/libraries?topRated=true')}
                      sx={{
                        p: { xs: 1, md: 2 },
                        borderRadius: 3,
                        bgcolor: 'background.paper',
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        border: '2px solid',
                        borderColor: 'transparent',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 24px rgba(255, 193, 7, 0.25)',
                          borderColor: '#FFC107',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: 32, md: 44 },
                          height: { xs: 32, md: 44 },
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #FFC107 0%, #FFD54F 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto',
                          mb: { xs: 0.5, md: 1 },
                        }}
                      >
                        <EmojiEvents sx={{ color: 'white', fontSize: { xs: 18, md: 24 } }} />
                      </Box>
                      <Typography variant="subtitle2" fontWeight="700" fontSize={{ xs: '0.65rem', md: '0.85rem' }}>
                        Top Rated
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontSize={{ xs: '0.55rem', md: '0.7rem' }}>
                        Best libraries
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={3} sm={3} md={3}>
                    <Box
                      onClick={() => navigate('/libraries?oneDayPass=true')}
                      sx={{
                        p: { xs: 1, md: 2 },
                        borderRadius: 3,
                        bgcolor: 'background.paper',
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        border: '2px solid',
                        borderColor: 'transparent',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 24px rgba(76, 175, 80, 0.25)',
                          borderColor: '#4CAF50',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: 32, md: 44 },
                          height: { xs: 32, md: 44 },
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #4CAF50 0%, #81C784 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto',
                          mb: { xs: 0.5, md: 1 },
                        }}
                      >
                        <Today sx={{ color: 'white', fontSize: { xs: 18, md: 24 } }} />
                      </Box>
                      <Typography variant="subtitle2" fontWeight="700" fontSize={{ xs: '0.65rem', md: '0.85rem' }}>
                        Day Pass
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontSize={{ xs: '0.55rem', md: '0.7rem' }}>
                        One day access
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={3} sm={3} md={3}>
                    <Box
                      onClick={() => navigate('/institutes')}
                      sx={{
                        p: { xs: 1, md: 2 },
                        borderRadius: 3,
                        bgcolor: 'background.paper',
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        border: '2px solid',
                        borderColor: 'transparent',
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 12px 24px rgba(156, 39, 176, 0.25)',
                          borderColor: '#9C27B0',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: 32, md: 44 },
                          height: { xs: 32, md: 44 },
                          borderRadius: 2,
                          background: 'linear-gradient(135deg, #9C27B0 0%, #CE93D8 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto',
                          mb: { xs: 0.5, md: 1 },
                        }}
                      >
                        <School sx={{ color: 'white', fontSize: { xs: 18, md: 24 } }} />
                      </Box>
                      <Typography variant="subtitle2" fontWeight="700" fontSize={{ xs: '0.65rem', md: '0.85rem' }}>
                        Institutes
                      </Typography>
                      <Typography variant="caption" color="text.secondary" fontSize={{ xs: '0.55rem', md: '0.7rem' }}>
                        Find institutes
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </MotionBox>

              {/* Nearest Libraries */}
              <MotionBox
                variants={itemVariants}
                mb={{ xs: 2, md: 3 }}
                sx={{ width: '100%', maxWidth: '100%', overflowX: 'hidden' }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Box>
                    <Typography variant="h6" fontWeight="800">
                      {locationStatus === 'granted' ? 'Nearest Libraries' : 'Libraries Near You'}
                    </Typography>
                    {showFallbackCaption && (
                      <Typography variant="caption" color="text.secondary">
                        Showing closest matches — expand the radius to see more nearby.
                      </Typography>
                    )}
                  </Box>
                  <Box display="flex" gap={1}>
                    <IconButton
                      onClick={() => scrollCarousel('left')}
                      size="small"
                      disabled={librariesStatus === 'loading'}
                      aria-label="Scroll libraries left"
                      sx={{
                        bgcolor: 'action.hover',
                        opacity: librariesStatus === 'loading' ? 0.4 : 1,
                        pointerEvents: librariesStatus === 'loading' ? 'none' : 'auto',
                        '&:hover': { bgcolor: 'action.selected' },
                      }}
                    >
                      <ChevronLeft />
                    </IconButton>
                    <IconButton
                      onClick={() => scrollCarousel('right')}
                      size="small"
                      disabled={librariesStatus === 'loading'}
                      aria-label="Scroll libraries right"
                      sx={{
                        bgcolor: 'action.hover',
                        opacity: librariesStatus === 'loading' ? 0.4 : 1,
                        pointerEvents: librariesStatus === 'loading' ? 'none' : 'auto',
                        '&:hover': { bgcolor: 'action.selected' },
                      }}
                    >
                      <ChevronRight />
                    </IconButton>
                  </Box>
                </Box>

                <Box
                  ref={carouselRef}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: { xs: 1.5, md: 2 },
                    overflowX: 'auto',
                    scrollBehavior: 'smooth',
                    pb: 1.5,
                    '&::-webkit-scrollbar': { display: 'none' },
                    scrollbarWidth: 'none',
                  }}
                >
                  {renderLibrariesCarouselContent()}
                </Box>

                {librariesStatus === 'success' && (
                  <Box textAlign="center" mt={2}>
                    <Button
                      variant="outlined"
                      size="medium"
                      onClick={() => navigate('/libraries')}
                      sx={{
                        borderRadius: 3,
                        px: 4,
                        py: 1.5,
                        textTransform: 'none',
                        fontWeight: 600,
                        borderWidth: 2,
                        '&:hover': { borderWidth: 2 },
                      }}
                    >
                      View All Libraries
                    </Button>
                  </Box>
                )}
              </MotionBox>

              {/* Discount Filters */}
              <MotionBox variants={itemVariants} mb={{ xs: 3, md: 4 }}>
                <Typography variant="subtitle1" fontWeight="700" mb={2} color="text.secondary">
                  First-Time Discounts
                </Typography>
                <Grid container spacing={1.5}>
                  {[10, 20, 30, 40].map((discount, i) => (
                    <Grid item xs={3} key={discount}>
                      <DiscountTile
                        discount={discount}
                        tier={i}
                        onClick={() => navigate(`/libraries?minDiscount=${discount}`)}
                      />
                    </Grid>
                  ))}
                </Grid>
              </MotionBox>

              {/* City Filters */}
              <MotionBox variants={itemVariants} mb={{ xs: 4, md: 6 }}>
                <CityFilterSection
                  detectedCity={detectedCity}
                  nearbyCities={nearbyCities}
                  selectedCity={selectedCity}
                  onCitySelect={selectCity}
                  isDetecting={isDetecting}
                  showAllCitiesOption={true}
                />
              </MotionBox>

              {/* Search Radius — only when location was granted */}
              {locationStatus === 'granted' && (
                <MotionBox variants={itemVariants} mb={{ xs: 4, md: 6 }}>
                  <Typography variant="subtitle1" fontWeight="700" mb={2} color="text.secondary">
                    Search Radius
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: RADIUS.xl,
                      bgcolor: 'background.paper',
                    }}
                  >
                    <SearchRadius value={nearbyRadius} onChange={setNearbyRadius} />
                  </Box>
                </MotionBox>
              )}

              {/* Benefit Grid — lazy loaded */}
              <MotionBox variants={itemVariants} sx={{ mt: { xs: 4, md: 6 } }}>
                <Suspense fallback={<BenefitGridSkeleton />}>
                  <BenefitGrid />
                </Suspense>
              </MotionBox>

              {/* Three Feature Cards - Desktop Only */}
              <MotionBox
                variants={itemVariants}
                sx={{ mt: { xs: 6, md: 8 }, display: { xs: 'none', md: 'block' } }}
              >
                <Grid container spacing={{ xs: 2, md: 4 }}>
                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        height: '100%',
                        borderRadius: 4,
                        p: { xs: 3, md: 4 },
                        background: 'linear-gradient(135deg, rgba(242, 101, 34, 0.05) 0%, rgba(201, 78, 16, 0.05) 100%)',
                        border: '1px solid',
                        borderColor: 'divider',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 12px 24px rgba(242, 101, 34, 0.15)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: 3,
                          background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 3,
                        }}
                      >
                        <PanTool sx={{ fontSize: 40, color: 'white' }} />
                      </Box>
                      <Typography variant="h5" fontWeight="800" gutterBottom>
                        Library in your palm
                      </Typography>
                      <Typography variant="body2" color="text.secondary" lineHeight={1.8}>
                        Like everything around that has made use of technology and innovation and have gone portable, we have made your choice for best libraries portable as well! No need to physically visit libraries and enquire about the seats now, just tap on your phone and your will have the library availability right there!
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        height: '100%',
                        borderRadius: 4,
                        p: { xs: 3, md: 4 },
                        background: 'linear-gradient(135deg, rgba(33, 150, 243, 0.05) 0%, rgba(100, 181, 246, 0.05) 100%)',
                        border: '1px solid',
                        borderColor: 'divider',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 12px 24px rgba(33, 150, 243, 0.15)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: 3,
                          background: 'linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 3,
                        }}
                      >
                        <Checklist sx={{ fontSize: 40, color: 'white' }} />
                      </Box>
                      <Typography variant="h5" fontWeight="800" gutterBottom>
                        EASE OF SELECTION
                      </Typography>
                      <Typography variant="body2" color="text.secondary" lineHeight={1.8}>
                        With more than 1000+ libraries in delhi-NCR, we aim to provide you with the largest pool of libraries so that you can make an informed decision about where you want to study!
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Box
                      sx={{
                        height: '100%',
                        borderRadius: 4,
                        p: { xs: 3, md: 4 },
                        background: 'linear-gradient(135deg, rgba(233, 30, 99, 0.05) 0%, rgba(240, 98, 146, 0.05) 100%)',
                        border: '1px solid',
                        borderColor: 'divider',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 12px 24px rgba(233, 30, 99, 0.15)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: 3,
                          background: 'linear-gradient(135deg, #e91e63 0%, #f06292 100%)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 3,
                        }}
                      >
                        <Map sx={{ fontSize: 40, color: 'white' }} />
                      </Box>
                      <Typography variant="h5" fontWeight="800" gutterBottom>
                        Study Across Multiple Locations
                      </Typography>
                      <Typography variant="body2" color="text.secondary" lineHeight={1.8}>
                        We have libraries all across delhi-NCR, so you can switch between 5 different libraries in a month according to your liking and preferences through one single plan.
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </MotionBox>

              {/* Add Your Library CTA - Desktop Only */}
              <MotionBox
                variants={itemVariants}
                sx={{ mt: { xs: 6, md: 8 }, display: { xs: 'none', md: 'block' } }}
              >
                <Box
                  component="a"
                  href="mailto:contact@bookmylibrary.in?subject=Register My Library/Institute"
                  sx={{
                    display: 'block',
                    textDecoration: 'none',
                    borderRadius: 4,
                    p: { xs: 4, md: 5 },
                    background: 'linear-gradient(135deg, rgba(76, 175, 80, 0.08) 0%, rgba(102, 187, 106, 0.08) 100%)',
                    border: '2px solid',
                    borderColor: 'success.light',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 24px rgba(76, 175, 80, 0.2)',
                      borderColor: 'success.main',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <Box
                      sx={{
                        width: 80,
                        height: 80,
                        borderRadius: 3,
                        background: 'linear-gradient(135deg, #4caf50 0%, #66bb6a 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <Business sx={{ fontSize: 40, color: 'white' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h5" fontWeight="800" gutterBottom color="text.primary">
                        Own a Library or Study Space?
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                        Join BookMyLibrary and reach thousands of students looking for the perfect study environment. List your library for free!
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight="600"
                        sx={{ color: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        Register Your Library Now →
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </MotionBox>

              {/* Footer Section */}
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)',
                  color: 'white',
                  mt: { xs: 4, md: 10 },
                  py: { xs: 4, md: 8 },
                  width: '100%',
                  maxWidth: '100vw',
                  overflowX: 'hidden',
                  borderRadius: 4,
                  px: { xs: 2, md: 4 },
                  mb: { xs: 8, md: 0 },
                }}
              >
                <Grid container spacing={{ xs: 2, md: 6 }}>
                  <Grid item xs={12} md={5}>
                    <Typography variant="h5" fontWeight="800" gutterBottom>
                      About BookMyLibrary
                    </Typography>
                    <Typography variant="body1" sx={{ opacity: 0.95, lineHeight: 1.8, mb: 3 }}>
                      Ever heard of choosing a suitable library to visit? When everything from clothes, books, groceries, tutions and what not is available online then why not libraries and study spaces too? Yes you heard it right! Now you can book your seats at your dream library which you might have not discovered till now sitting at your place.
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={2}>
                    <Typography variant="h6" fontWeight="700" gutterBottom>
                      Quick Links
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Typography
                        component="a"
                        href="/libraries"
                        sx={{
                          color: 'white',
                          textDecoration: 'none',
                          opacity: 0.9,
                          '&:hover': { opacity: 1, textDecoration: 'underline' },
                        }}
                      >
                        Browse Libraries
                      </Typography>
                      <Typography
                        component="a"
                        href="https://blog.bookmylibrary.in/"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          color: 'white',
                          textDecoration: 'none',
                          opacity: 0.9,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                          '&:hover': { opacity: 1, textDecoration: 'underline' },
                        }}
                      >
                        <Article sx={{ fontSize: 16 }} />
                        Blog
                      </Typography>
                      <Typography
                        component="a"
                        href="mailto:contact@bookmylibrary.in?subject=Register My Library/Institute"
                        sx={{
                          color: 'white',
                          textDecoration: 'none',
                          opacity: 0.9,
                          '&:hover': { opacity: 1, textDecoration: 'underline' },
                        }}
                      >
                        Add Your Library
                      </Typography>
                      <Typography
                        component="a"
                        href="/privacy"
                        sx={{
                          color: 'white',
                          textDecoration: 'none',
                          opacity: 0.9,
                          '&:hover': { opacity: 1, textDecoration: 'underline' },
                        }}
                      >
                        Privacy Policy
                      </Typography>
                      <Typography
                        component="a"
                        href="/terms"
                        sx={{
                          color: 'white',
                          textDecoration: 'none',
                          opacity: 0.9,
                          '&:hover': { opacity: 1, textDecoration: 'underline' },
                        }}
                      >
                        Terms of Use
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={5}>
                    <Typography variant="h5" fontWeight="800" gutterBottom>
                      Contact Us
                    </Typography>

                    <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          bgcolor: 'rgba(255,255,255,0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Phone fontSize="small" />
                      </Box>
                      <Box>
                        <Typography variant="caption" display="block" sx={{ opacity: 0.8, mb: 0.5 }}>
                          Phone
                        </Typography>
                        <Typography
                          component="a"
                          href="tel:+918595675155"
                          sx={{
                            display: 'block',
                            color: 'white',
                            textDecoration: 'none',
                            fontWeight: 600,
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          +91 8595 675 155
                        </Typography>
                        <Typography
                          component="a"
                          href="tel:+15559855243"
                          sx={{
                            display: 'block',
                            color: 'white',
                            textDecoration: 'none',
                            fontWeight: 600,
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          +1 555 985 5243
                        </Typography>
                      </Box>
                    </Box>

                    <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          bgcolor: 'rgba(255,255,255,0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Email fontSize="small" />
                      </Box>
                      <Box sx={{ overflow: 'hidden', width: '100%' }}>
                        <Typography variant="caption" display="block" sx={{ opacity: 0.8, mb: 0.5 }}>
                          Email
                        </Typography>
                        <Typography
                          component="a"
                          href="mailto:info@bookmylibrary.in"
                          sx={{
                            color: 'white',
                            textDecoration: 'none',
                            fontWeight: 600,
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            maxWidth: '100%',
                            display: 'block',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          info@bookmylibrary.in
                        </Typography>
                      </Box>
                    </Box>

                    <Box display="flex" alignItems="flex-start" gap={2}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          bgcolor: 'rgba(255,255,255,0.15)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <LocationOn fontSize="small" />
                      </Box>
                      <Box>
                        <Typography variant="caption" display="block" sx={{ opacity: 0.8, mb: 0.5 }}>
                          Address
                        </Typography>
                        <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
                          H 74 GF Block-B Pocket-10, Sector-23, Dwarka, New Delhi - 110078
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                </Grid>

                <Box textAlign="center" mt={6} pt={4} borderTop="1px solid rgba(255,255,255,0.15)">
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    © {new Date().getFullYear()} BookMyLibrary. All rights reserved.
                  </Typography>
                </Box>
              </Box>

            </MotionBox>
          </Container>
        </Box>

        {/* Bottom Navigation - Mobile Only */}
        {isAuthenticated && (
          <Paper
            elevation={8}
            sx={{
              position: 'fixed',
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1000,
              display: { xs: 'block', md: 'none' },
              borderTopLeftRadius: 20,
              borderTopRightRadius: 20,
              overflow: 'hidden',
              background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)',
            }}
          >
            <BottomNavigation
              value={bottomNavValue}
              onChange={(event, newValue) => {
                setBottomNavValue(newValue);
                const routes = ['/', '/student/access', '/student/profile'];
                navigate(routes[newValue]);
              }}
              showLabels
              sx={{
                background: 'transparent',
                height: 70,
                '& .MuiBottomNavigationAction-root': {
                  color: 'rgba(255, 255, 255, 0.6)',
                  minWidth: 'auto',
                  padding: '6px 12px',
                  transition: 'all 0.3s ease',
                  '&:hover': { color: 'rgba(255, 255, 255, 0.9)' },
                  '&.Mui-selected': {
                    color: 'white',
                    fontSize: '0.75rem',
                    paddingTop: '6px',
                    '& .MuiBottomNavigationAction-label': {
                      fontSize: '0.75rem',
                      fontWeight: 700,
                    },
                    '& .MuiSvgIcon-root': {
                      transform: 'scale(1.2)',
                      filter: 'drop-shadow(0 2px 8px rgba(255, 255, 255, 0.3))',
                    },
                  },
                },
                '& .MuiBottomNavigationAction-label': {
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  marginTop: '4px',
                },
              }}
            >
              <BottomNavigationAction label="Home" icon={<Home />} />
              <BottomNavigationAction label="Access" icon={<QrCode2 />} />
              <BottomNavigationAction label="Profile" icon={<Person />} />
            </BottomNavigation>
          </Paper>
        )}
      </Box>
    </>
  );
}

export default PublicHome;
